import { useState } from "react";
import { formatBytes, formatRelativeTime } from "@saucer/core";
import type { LinkMode, SaucerFile, Visibility } from "@saucer/core";
import { api } from "../api.js";

interface FileListProps {
  files: SaucerFile[];
  onChanged: (file: SaucerFile) => void;
  onDeleted: (id: string) => void;
}

export function FileList({ files, onChanged, onDeleted }: FileListProps) {
  if (files.length === 0) {
    return (
      <p className="empty meta">
        Nothing here yet. Drop a file above, or run <code className="mono">npx saucer-sh send</code>.
      </p>
    );
  }

  return (
    <ul className="files">
      {files.map((file) => (
        <FileRow key={file.id} file={file} onChanged={onChanged} onDeleted={onDeleted} />
      ))}
    </ul>
  );
}

function FileRow({ file, onChanged, onDeleted }: { file: SaucerFile } & Omit<FileListProps, "files">) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const setMode = async (linkMode: LinkMode) => {
    setBusy(true);
    try {
      onChanged(await api.updateFile(file.id, { linkMode }));
    } finally {
      setBusy(false);
    }
  };

  const setVisibility = async (visibility: Visibility) => {
    setBusy(true);
    try {
      onChanged(await api.updateFile(file.id, { visibility }));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Delete ${file.filename}? The link stops working immediately.`)) return;
    setBusy(true);
    try {
      await api.deleteFile(file.id);
      onDeleted(file.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="files__row">
      <span className="files__thumb" aria-hidden="true">
        {file.thumbnailUrl ? (
          <img src={`${file.url}/img?w=96&h=96&fit=cover`} alt={file.alt ?? ""} loading="lazy" />
        ) : (
          <span className="files__kind mono">{KIND_LABEL[file.kind]}</span>
        )}
      </span>

      <span className="files__body">
        <a className="files__link mono" href={file.url} target="_blank" rel="noreferrer">
          /{file.owner}/{file.slug}
        </a>
        <span className="meta">
          {formatBytes(file.size)} · {formatRelativeTime(file.createdAt)} · {file.downloads}{" "}
          {file.downloads === 1 ? "open" : "opens"}
          {file.pipelineState === "queued" || file.pipelineState === "running"
            ? " · processing"
            : ""}
        </span>
        {file.visibility === "private" ? (
          <span className="meta mono">private · only you can open it</span>
        ) : null}
        {file.protected ? <span className="meta mono">locked · needs password</span> : null}
        {file.title ? <span className="files__title">{file.title}</span> : null}
        {file.tags.length > 0 ? (
          <span className="files__tags">
            {file.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="chip">
                {tag}
              </span>
            ))}
          </span>
        ) : null}
      </span>

      <span className="files__actions">
        <span className="seg" role="group" aria-label="What opening the link does">
          <span className="seg__label mono">link opens</span>
          <button
            className={`seg__option${file.linkMode === "preview" ? " seg__option--on" : ""}`}
            type="button"
            disabled={busy}
            title="The link shows a page with the file in it"
            onClick={() => file.linkMode !== "preview" && setMode("preview")}
          >
            a page
          </button>
          <button
            className={`seg__option${file.linkMode === "raw" ? " seg__option--on" : ""}`}
            type="button"
            disabled={busy}
            title="The link serves the file bytes directly, no page around it"
            onClick={() => file.linkMode !== "raw" && setMode("raw")}
          >
            the file
          </button>
        </span>
        <span className="seg" role="group" aria-label="Who can open the link">
          <span className="seg__label mono">opens for</span>
          <button
            className={`seg__option${file.visibility === "public" ? " seg__option--on" : ""}`}
            type="button"
            disabled={busy}
            title="Anyone who has the link can open it"
            onClick={() => file.visibility !== "public" && setVisibility("public")}
          >
            anyone
          </button>
          <button
            className={`seg__option${file.visibility === "private" ? " seg__option--on" : ""}`}
            type="button"
            disabled={busy}
            title="The link only opens while you are signed in; everyone else gets a 404"
            onClick={() => file.visibility !== "private" && setVisibility("private")}
          >
            only you
          </button>
        </span>
        <button
          className="button button--quiet"
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(file.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          className="button button--quiet button--danger"
          type="button"
          disabled={busy}
          onClick={remove}
        >
          Delete
        </button>
      </span>
    </li>
  );
}

const KIND_LABEL: Record<SaucerFile["kind"], string> = {
  image: "img",
  video: "vid",
  audio: "aud",
  pdf: "pdf",
  text: "txt",
  archive: "zip",
  binary: "bin",
};
